import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel, ButtonSize } from '@vizit/atoms';
import { BENCHMARK_DESCRIPTION_MAX_LENGTH, BENCHMARK_NAME_MAX_LENGTH } from '@vizit/constants';
import { FormDialog, FormError, FormTextArea, FormTextField } from '@vizit/forms';
import { PlusIcon } from '@vizit/icons';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { ImageTypeField } from '../image-type-field';

const DEFAULT_SUBMISSION_ERROR = 'Error creating benchmark. Please try again in a few minutes.';

const BENCHMARK_IMAGE_TYPES = ['hero', 'carousel'] as const;

type BenchmarkImageType = (typeof BENCHMARK_IMAGE_TYPES)[number];

type BenchmarkFormSchema = {
  name: string;
  description: string;
  imageType: BenchmarkImageType;
};

const EMPTY_FORM: BenchmarkFormSchema = {
  name: '',
  description: '',
  imageType: undefined as unknown as BenchmarkImageType,
};

const formSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required('Benchmark name is required')
    .max(BENCHMARK_NAME_MAX_LENGTH, `Benchmark name must be ${BENCHMARK_NAME_MAX_LENGTH} characters or fewer`),
  description: yup
    .string()
    .trim()
    .defined()
    .max(
      BENCHMARK_DESCRIPTION_MAX_LENGTH,
      `Description must be ${BENCHMARK_DESCRIPTION_MAX_LENGTH} characters or fewer`,
    ),
  imageType: yup.string().oneOf(BENCHMARK_IMAGE_TYPES, 'Image type is required').required('Image type is required'),
});

interface BenchmarkDialogProps {
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Whether to hide the trigger button. Set when the caller renders its own
   * "Create benchmark" button and controls `isOpen`.
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  hideTrigger?: boolean;
  /**
   * Called with the validated form values. Throw an `Error` to keep the dialog
   * open and show its message under the form; the message stays until the
   * next attempt succeeds or the dialog closes, and the user can retry as is.
   */
  onSubmit: (data: BenchmarkFormSchema) => Promise<void>;
  /**
   * Called when the dialog opens or closes
   *
   * @optional
   * @param isOpen - Whether the dialog is open
   */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Dialog for creating a custom benchmark: a required name, an optional
 * description and the image type it grades (Hero or Carousel). Images and the
 * product category are added later on the benchmark page, so they are not
 * asked for here.
 *
 * @example
 * <BenchmarkDialog isOpen={isOpen} onOpenChange={setIsOpen} hideTrigger onSubmit={createBenchmark} />
 */
function BenchmarkDialog(props: BenchmarkDialogProps) {
  const { isOpen, hideTrigger = false, onSubmit, onOpenChange } = props;

  const formMethods = useForm<BenchmarkFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: EMPTY_FORM,
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, reset, clearErrors } = formMethods;

  // Kept outside react-hook-form on purpose: `setError` (even on `root`) flips
  // `isValid` to false, which would disable the submit button until a field
  // changes, so a failed request could not simply be retried.
  const [submissionError, setSubmissionError] = React.useState<string | null>(null);

  const handleOnResetForm = () => {
    clearErrors();
    setSubmissionError(null);
    reset(EMPTY_FORM);
  };

  const handleFormSubmit = async (data: BenchmarkFormSchema) => {
    try {
      // Pick the fields explicitly: FormDialog registers a field array under the
      // form name, which would otherwise ride along in the submitted data.
      await onSubmit({ name: data.name.trim(), description: data.description.trim(), imageType: data.imageType });
    } catch (error) {
      setSubmissionError(error instanceof Error && error.message ? error.message : DEFAULT_SUBMISSION_ERROR);
    }
  };

  return (
    <FormDialog
      formMethods={formMethods}
      formName="benchmark"
      isOpen={isOpen}
      onSubmit={handleSubmit(handleFormSubmit)}
      onResetForm={handleOnResetForm}
      onOpenChange={onOpenChange}
      data-testid="benchmark-dialog"
    >
      <FormDialog.Trigger>
        {hideTrigger ? (
          <Button className="sr-only" aria-hidden="true" isDisabled>
            Open dialog
          </Button>
        ) : (
          <Button
            level={ButtonLevel.Primary}
            size={ButtonSize.Lg}
            iconStart={<PlusIcon size={20} aria-hidden />}
            data-testid="benchmark-trigger"
          >
            Create benchmark
          </Button>
        )}
      </FormDialog.Trigger>
      <FormDialog.Title>New benchmark</FormDialog.Title>
      <FormDialog.Subtitle>
        Name it and pick the image type it grades. You'll add images and choose a product category next.
      </FormDialog.Subtitle>
      <FormDialog.FormFields>
        <div className="flex flex-col gap-10">
          <FormTextField
            control={control}
            name="name"
            label="Benchmark name"
            placeholder="Enter benchmark name"
            isRequired
            maxLength={BENCHMARK_NAME_MAX_LENGTH}
            className="min-h-0"
            inputProps={{ 'data-testid': 'benchmark-name-input' }}
          />
          <FormTextArea
            control={control}
            name="description"
            label={
              <>
                Description <span className="font-regular text-tertiary">(optional)</span>
              </>
            }
            placeholder="What is this benchmark for?"
            rows={3}
            maxLength={BENCHMARK_DESCRIPTION_MAX_LENGTH}
            className="min-h-0"
            textAreaProps={{ 'data-testid': 'benchmark-description-input' }}
          />
          <ImageTypeField control={control} name="imageType" isRequired />
          <FormError message={submissionError} role="alert" />
        </div>
      </FormDialog.FormFields>
      <FormDialog.SubmitButton data-testid="benchmark-submit">Create benchmark</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { BenchmarkDialog };
export type { BenchmarkDialogProps, BenchmarkFormSchema };
